import { useQueries } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import type { SearchResult } from '@/src/components/global-search/types'
import type { GetAssetDataResponse } from '@/src/services/token-info/asset-data'
import { searchHistoryAtom } from '@/src/stores/global-search/atom'
import { assetDataQuery } from '../keys/token-info/asset-data'

/**
 * This hook fetches detailed asset data for all items in the search history.
 * It returns price change percentage and current USD price for each item.
 * For failed queries, it returns the raw search history items.
 *
 * @returns A tuple with [processed asset data array, function to add to search history]
 */
export function useSearchHistoryAssetData(): [
  SearchResult[],
  (item: SearchResult) => void,
] {
  const [searchHistory, addToSearchHistory] = useAtom(searchHistoryAtom)

  const queryResults = useQueries({
    queries: searchHistory.map((item) => ({
      ...assetDataQuery({
        chainId: item.chainId,
        denom: item.denom,
      }),
      select: (data: GetAssetDataResponse) => ({
        chainId: item.chainId,
        denom: item.denom,
        name: item.name,
        symbol: item.symbol,
        icon: item.icon,
        change: data.market_data.price_change_percentage_24h,
        price: data.market_data.current_price.usd.toString(),
      }),
    })),
  })

  // Create a map of successful query results
  const successfulResults = new Map<string, SearchResult>()
  queryResults.forEach((result, index) => {
    if (result.isSuccess && result.data) {
      const item = searchHistory[index]
      const key = `${item.chainId}-${item.denom}`
      successfulResults.set(key, result.data as SearchResult)
    }
  })

  // Merge successful results with raw history items for failed queries
  const mergedResults = searchHistory.map((item) => {
    const key = `${item.chainId}-${item.denom}`
    return successfulResults.get(key) || item
  })

  return [mergedResults, addToSearchHistory]
}
